import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui';
import {
  FIELD_OF_WORK_OPTIONS,
  FIELD_OF_WORK_OTHER,
  resolveFieldOfWorkSelection,
  resolveFieldOfWorkValue,
} from '../utils';

const selectClassName =
  'w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[48px] text-base md:px-3 md:py-2 md:min-h-0';

interface FieldOfWorkFieldProps {
  value: string;
  onChange: (fieldOfWork: string) => void;
  required?: boolean;
  id?: string;
}

export const FieldOfWorkField: React.FC<FieldOfWorkFieldProps> = ({
  value,
  onChange,
  required = false,
  id = 'field-of-work-select',
}) => {
  const initial = resolveFieldOfWorkSelection(value);
  const [selection, setSelection] = useState(initial.selection);
  const [customValue, setCustomValue] = useState(initial.customValue);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const next = resolveFieldOfWorkSelection(value);
    setSelection(next.selection);
    setCustomValue(next.customValue);
    lastEmitted.current = value;
  }, [value]);

  const emit = (nextSelection: string, nextCustom: string) => {
    const resolved = resolveFieldOfWorkValue(nextSelection, nextCustom);
    lastEmitted.current = resolved;
    onChange(resolved);
  };

  const isOther = selection === FIELD_OF_WORK_OTHER;

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          Field of Work
        </label>
        <select
          id={id}
          value={selection}
          onChange={e => {
            const nextSelection = e.target.value;
            setSelection(nextSelection);
            if (nextSelection === FIELD_OF_WORK_OTHER) {
              emit(FIELD_OF_WORK_OTHER, customValue);
              return;
            }
            setCustomValue('');
            emit(nextSelection, '');
          }}
          required={required && !isOther}
          className={selectClassName}
        >
          <option value="">Select field of work</option>
          {FIELD_OF_WORK_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {isOther && (
        <Input
          label="Specify field of work"
          name={`${id}-other`}
          value={customValue}
          onChange={e => {
            const nextCustom = e.target.value;
            setCustomValue(nextCustom);
            emit(FIELD_OF_WORK_OTHER, nextCustom);
          }}
          placeholder="e.g., Disability Support"
          required={required}
        />
      )}
    </div>
  );
};
